import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel } from '@mui/material';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { closeModal } from 'features/modal/modalSlice';
import { ModalName } from 'features/modal/types';
import { whiteboardClosed } from 'features/layout/features/content/actions';
import { useWhiteboard } from 'features/whiteboard/hooks/useWhiteboard';
import { selectWhiteboardId } from 'features/whiteboard/whiteboardSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useDontShowAgain } from 'hooks/useDontShowAgain';

type CloseWhiteboardDialogProps = {
  modalName: ModalName;
  translationKey: 'close_whiteboard_confirmation' | 'close_restorable_whiteboard';
  offerDontShowAgain?: boolean;
};

const CloseWhiteboardDialog = ({
  modalName,
  translationKey,
  offerDontShowAgain = false,
}: CloseWhiteboardDialogProps) => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const currentWhiteboardId = useAppSelector(selectWhiteboardId);

  const { isFeatureAvailable } = useWhiteboard();

  const shouldClose = !isFeatureAvailable;

  const confirm = () => {
    if (currentWhiteboardId) {
      dispatch(whiteboardClosed(currentWhiteboardId));
    }
    dispatch(closeModal(modalName));
  };

  const { dontShowAgain, handleChange, savePreference } = useDontShowAgain({
    modalName,
    onConfirm: confirm,
  });

  useEffect(() => {
    if (shouldClose) {
      dispatch(closeModal(modalName));
    }
  }, [shouldClose, dispatch, modalName]);

  const handleOk = () => {
    if (offerDontShowAgain) {
      savePreference();
    }

    confirm();
  };

  const handleCancel = () => {
    dispatch(closeModal(modalName));
  };

  return (
    <ConfirmationDialog
      open={!shouldClose}
      maxWidth="sm"
      title={t(`${translationKey}.title`)}
      description={t(`${translationKey}.description`)}
      okText={t(`${translationKey}.btn_ok_label`)}
      onOk={handleOk}
      cancelText={t(`${translationKey}.btn_cancel_label`)}
      onCancel={handleCancel}
      preventClose
      hideCloseButton
    >
      {offerDontShowAgain && (
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(event) => handleChange(event.target.checked)}
              color="primary"
            />
          }
          label={t(`${translationKey}.dont_show_again`)}
          sx={{ mt: 2 }}
        />
      )}
    </ConfirmationDialog>
  );
};

export default CloseWhiteboardDialog;
